import React from "react";
import { Box, Button, Chip, Grid2, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import type { HeroSettings, HeroProductRow } from "../../api/adminHeroApi";

type Props = {
  hero: Partial<HeroSettings>;
  products: HeroProductRow[];
};

function bgStyle(hero: Partial<HeroSettings>) {
  if (hero.background_type === "gradient") {
    return { background: hero.gradient_css || "linear-gradient(135deg, #0a0a0f, #1a1a2e)" };
  }
  if (hero.background_type === "image") {
    return {
      backgroundImage: hero.background_url ? `url(${hero.background_url})` : "none",
      backgroundSize: "cover",
      backgroundPosition: "center",
    };
  }
  return { background: "linear-gradient(135deg, #0a0a0f, #1a1a2e)" };
}

export default function HeroPreview({ hero, products }: Props) {
  const navigate = useNavigate();
  const overlayOpacity = Math.min(Math.max(Number(hero.overlay_opacity ?? 0.4), 0), 1);

  const layoutType = hero.hero_layout_type || "hero";
  const contentAlign = hero.content_align || "left";
  const contentPosition = hero.content_position || "left";
  const mediaPosition = hero.media_position || "right";
  const bannerHeight = hero.banner_height || "standard";

  const primaryCtaText = hero.primary_cta_text?.trim();
  const primaryCtaLink = hero.primary_cta_link?.trim();
  const secondaryCtaText = hero.secondary_cta_text?.trim();
  const secondaryCtaLink = hero.secondary_cta_link?.trim();

  const hasPrimaryCta = Boolean(primaryCtaText && primaryCtaLink);
  const hasSecondaryCta = Boolean(secondaryCtaText && secondaryCtaLink);

  const justifyMap = {
    left: "flex-start",
    center: "center",
    right: "flex-end",
  } as const;

  const textAlignMap = {
    left: "left",
    center: "center",
    right: "right",
  } as const;

  const alignItemsMap = {
    left: "flex-start",
    center: "center",
    right: "flex-end",
  } as const;

  const heightMap = {
    compact: layoutType === "hero" ? 320 : 130,
    standard: layoutType === "hero" ? 400 : 180,
    tall: layoutType === "hero" ? 500 : 240,
  } as const;

  const showMedia =
    layoutType !== "mini_banner" &&
    mediaPosition !== "none" &&
    products.length > 0;

  const content = (
    <Stack
      spacing={layoutType === "mini_banner" ? 0.75 : 2}
      sx={{
        maxWidth: layoutType === "mini_banner" ? "100%" : 620,
        width: "100%",
        alignItems: alignItemsMap[contentAlign],
        textAlign: textAlignMap[contentAlign],
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent={justifyMap[contentAlign]}
        flexWrap="wrap"
      >
        {hero.carousel_enabled ? (
          <Chip
            size="small"
            label="Carousel Slide"
            sx={{
              fontWeight: 700,
              backgroundColor: "rgba(138, 43, 226, 0.18)",
              color: "#c9a7ff",
              border: "1px solid rgba(138, 43, 226, 0.35)",
            }}
          />
        ) : null}

        {hero.promo_enabled && hero.promo_text ? (
          <Chip
            size={layoutType === "mini_banner" ? "small" : "medium"}
            label={hero.promo_text}
            sx={{
              fontWeight: 700,
              backgroundColor: "rgba(0, 200, 255, 0.15)",
              color: "#00ccff",
              border: "1px solid rgba(0, 200, 255, 0.3)",
              backdropFilter: "blur(10px)",
            }}
          />
        ) : null}

        {hero.loyalty_highlight && layoutType !== "mini_banner" ? (
          <Chip
            label="Loyalty"
            sx={{
              fontWeight: 700,
              backgroundColor: "rgba(255, 215, 0, 0.15)",
              color: "#ffd700",
              border: "1px solid rgba(255, 215, 0, 0.3)",
              backdropFilter: "blur(10px)",
            }}
          />
        ) : null}
      </Stack>

      <Typography
        variant={layoutType === "hero" ? "h1" : "h2"}
        sx={{
          color: "white",
          fontWeight: 900,
          lineHeight: 1.1,
          fontSize:
            layoutType === "mini_banner"
              ? { xs: "1rem", md: "1.25rem" }
              : layoutType === "banner"
                ? { xs: "1.6rem", md: "2.2rem" }
                : { xs: "2rem", sm: "2.5rem", md: "3rem" },
          letterSpacing: "-0.02em",
          textShadow: "0 2px 10px rgba(0,0,0,0.35)",
        }}
      >
        {hero.headline || "Headline Preview"}
      </Typography>

      {hero.subheadline ? (
        <Typography
          sx={{
            color: "rgba(255,255,255,0.9)",
            fontSize:
              layoutType === "mini_banner"
                ? { xs: "0.85rem", md: "0.95rem" }
                : { xs: "1rem", md: "1.1rem" },
            maxWidth: 560,
            lineHeight: 1.6,
            whiteSpace: "pre-line",
          }}
        >
          {hero.subheadline}
        </Typography>
      ) : null}

      {(hasPrimaryCta || hasSecondaryCta) ? (
        <Stack
          direction="row"
          spacing={2}
          flexWrap="wrap"
          justifyContent={justifyMap[contentAlign]}
          sx={{ pt: layoutType === "mini_banner" ? 0.5 : 1 }}
        >
          {hasPrimaryCta ? (
            <Button
              variant="contained"
              onClick={() => {
                if (primaryCtaLink) navigate(primaryCtaLink);
              }}
              sx={{
                fontWeight: 800,
                px: layoutType === "mini_banner" ? 2.5 : 4,
                py: layoutType === "mini_banner" ? 0.9 : 1.4,
                background: "linear-gradient(135deg, #00ccff, #8a2be2)",
                color: "white",
                borderRadius: "40px",
              }}
            >
              {primaryCtaText}
            </Button>
          ) : null}

          {hasSecondaryCta && layoutType !== "mini_banner" ? (
            <Button
              variant="outlined"
              onClick={() => {
                if (secondaryCtaLink) navigate(secondaryCtaLink);
              }}
              sx={{
                fontWeight: 800,
                px: 4,
                py: 1.4,
                borderColor: "rgba(255,255,255,0.5)",
                color: "white",
                backgroundColor: "rgba(255,255,255,0.08)",
                borderRadius: "40px",
              }}
            >
              {secondaryCtaText}
            </Button>
          ) : null}
        </Stack>
      ) : null}
    </Stack>
  );

  const media = showMedia ? (
    <Box sx={{ width: "100%" }}>
      <Typography
        sx={{
          color: "rgba(255,255,255,0.7)",
          fontWeight: 700,
          fontSize: "0.8rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          mb: 1.5,
        }}
      >
        Featured Drops
      </Typography>

      <Box sx={{ display: "flex", gap: 2, overflowX: "auto", pb: 1 }}>
        {products.slice(0, layoutType === "banner" ? 2 : 4).map((fp) => (
          <Box
            key={fp.product_id}
            sx={{
              minWidth: layoutType === "banner" ? 150 : 180,
              maxWidth: layoutType === "banner" ? 150 : 180,
              background: "rgba(20,20,30,0.7)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: layoutType === "banner" ? 95 : 120,
                backgroundImage: fp.product.image_url ? `url(${fp.product.image_url})` : "none",
                backgroundSize: "cover",
                backgroundPosition: "center",
                bgcolor: "rgba(255,255,255,0.05)",
              }}
            />

            <Box sx={{ p: 1.5 }}>
              <Typography sx={{ color: "white", fontWeight: 800 }} noWrap>
                {fp.product.name}
              </Typography>

              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ color: "#00ccff", fontWeight: 700 }}>
                  ${fp.product.price}
                </Typography>

                {fp.badge_text ? (
                  <Chip
                    size="small"
                    label={fp.badge_text}
                    sx={{
                      height: 20,
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      background: "rgba(0, 200, 255, 0.2)",
                      color: "#00ccff",
                    }}
                  />
                ) : null}
              </Stack>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  ) : null;

  if (layoutType === "mini_banner") {
    return (
      <Box
        sx={{
          position: "relative",
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 0 0 1px rgba(255,255,255,0.05), 0 4px 20px rgba(0,0,0,0.5)",
          minHeight: 96,
          width: "100%",
          p: 2,
          ...bgStyle(hero),
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: `linear-gradient(90deg, rgba(0,0,0,${overlayOpacity + 0.2}) 0%, rgba(0,0,0,${overlayOpacity}) 100%)`,
          }}
        />

        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            minHeight: 64,
            display: "flex",
            alignItems: "center",
            justifyContent: justifyMap[contentPosition],
          }}
        >
          {content}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: 3,
        overflow: "hidden",
        boxShadow: "0 0 0 1px rgba(255,255,255,0.05), 0 4px 20px rgba(0,0,0,0.5)",
        minHeight: heightMap[bannerHeight],
        width: "100%",
        ...bgStyle(hero),
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: `linear-gradient(90deg, rgba(0,0,0,${overlayOpacity + 0.2}) 0%, rgba(0,0,0,${overlayOpacity}) 55%, rgba(0,0,0,${Math.max(overlayOpacity - 0.1, 0)}) 100%)`,
        }}
      />

      <Box sx={{ position: "relative", zIndex: 2, height: "100%" }}>
        <Grid2
          container
          direction={mediaPosition === "left" ? "row-reverse" : "row"}
          sx={{
            minHeight: heightMap[bannerHeight],
            alignItems: "center",
          }}
        >
          <Grid2
            size={{ xs: 12, md: showMedia ? 6 : 12 }}
            sx={{
              display: "flex",
              justifyContent: justifyMap[contentPosition],
              p: layoutType === "banner" ? { xs: 3, md: 4 } : { xs: 3, sm: 4 },
            }}
          >
            {content}
          </Grid2>

          {showMedia ? (
            <Grid2
              size={{ xs: 12, md: 6 }}
              sx={{
                display: "flex",
                alignItems: "center",
                p: layoutType === "banner" ? { xs: 3, md: 4 } : { xs: 3, sm: 4 },
              }}
            >
              {media}
            </Grid2>
          ) : null}
        </Grid2>
      </Box>
    </Box>
  );
}